'use client';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { supportCopy, SUPPORT_PRODUCT_OPTIONS } from '@/lib/i18n/pages/support';

const SUPPORT_EMAIL = 'support@datacode.app';

function buildSchema(v: {
  nameMin: string;
  emailInvalid: string;
  productRequired: string;
  messageMin: string;
}) {
  return z.object({
    name: z.string().min(2, v.nameMin),
    email: z.string().email(v.emailInvalid),
    product: z.string().min(1, v.productRequired),
    message: z.string().min(10, v.messageMin),
  });
}

type SupportFormData = z.infer<ReturnType<typeof buildSchema>>;

// Dropdown chevron. Positioned on the end side of the field: right in LTR,
// left in RTL.
const CHEVRON =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'white\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")';

export function SupportForm() {
  const { locale, dir } = useLocale();
  const isRtl = dir === 'rtl';
  const t = pageCopy(supportCopy, locale).form;
  const schema = useMemo(() => buildSchema(t.validation), [t.validation]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SupportFormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const functionUrl = process.env.NEXT_PUBLIC_SUPPORT_FUNCTION_URL || '/api/support';
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackEvent('support_submit', { status: 'success', product: data.product });
        reset();
      } else {
        setSubmitStatus('error');
        trackEvent('support_submit', { status: 'error', product: data.product });
      }
    } catch {
      setSubmitStatus('error');
      trackEvent('support_submit', { status: 'error', product: data.product });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldBase =
    'w-full border-b bg-transparent py-4 text-lg text-white outline-none transition-colors text-start';
  // Email is an LTR island inside the page direction: content and caret read
  // left to right, but on RTL pages the text hugs the start (right) edge so
  // it lines up with the labels and Arabic placeholders.
  const ltrIslandStyle: React.CSSProperties | undefined = isRtl ? { textAlign: 'right' } : undefined;
  // Global form rules pin inputs to a Latin face; on RTL pages they must use
  // the page's Arabic-script font (inherited from the body).
  const fontStyle: React.CSSProperties | undefined = isRtl ? { fontFamily: 'inherit' } : undefined;

  const errorClass = 'mt-2 text-sm text-red-500 text-start';

  const errorParts = t.error.split('{email}');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Two column layout for Name and Email */}
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-16 mb-16">
        {/* Name Field */}
        <div>
          <input
            {...register('name')}
            type="text"
            dir="auto"
            autoComplete="name"
            placeholder={t.namePlaceholder}
            aria-label={t.namePlaceholder}
            aria-invalid={errors.name ? 'true' : 'false'}
            style={fontStyle}
            className={cn(
              fieldBase,
              'placeholder:text-white/80',
              errors.name ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
          />
          {errors.name && (
            <p role="alert" className={errorClass}>{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <input
            {...register('email')}
            type="email"
            dir="ltr"
            autoComplete="email"
            placeholder={t.emailPlaceholder}
            aria-label={t.emailPlaceholder}
            aria-invalid={errors.email ? 'true' : 'false'}
            style={{ ...fontStyle, ...ltrIslandStyle }}
            className={cn(
              fieldBase,
              'placeholder:text-white/80',
              errors.email ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
          />
          {errors.email && (
            <p role="alert" className={errorClass}>{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Product Selection - Full Width */}
      <div className="mb-16">
        <select
          {...register('product')}
          aria-label={t.products.placeholder}
          aria-invalid={errors.product ? 'true' : 'false'}
          className={cn(
            fieldBase,
            'appearance-none cursor-pointer bg-no-repeat bg-[length:24px] ps-0 pe-8',
            errors.product ? 'border-red-500' : 'border-white/30 focus:border-white',
          )}
          style={{
            ...fontStyle,
            backgroundColor: 'transparent',
            backgroundImage: CHEVRON,
            backgroundPosition: isRtl ? 'left 0 center' : 'right 0 center',
          }}
        >
          <option value="" style={{ backgroundColor: '#173835', color: 'white' }}>
            {t.products.placeholder}
          </option>
          {SUPPORT_PRODUCT_OPTIONS.map((product) => (
            <option
              key={product.value}
              value={product.value}
              style={{ backgroundColor: '#173835', color: 'white' }}
            >
              {t.products[product.key]}
            </option>
          ))}
        </select>
        {errors.product && (
          <p role="alert" className={errorClass}>{errors.product.message}</p>
        )}
      </div>

      {/* Message Field - Full Width */}
      <div className="mb-16">
        <textarea
          {...register('message')}
          rows={3}
          dir="auto"
          placeholder={t.messagePlaceholder}
          aria-label={t.messagePlaceholder}
          aria-invalid={errors.message ? 'true' : 'false'}
          style={fontStyle}
          className={cn(
            fieldBase,
            'resize-none placeholder:text-white/80',
            errors.message ? 'border-red-500' : 'border-white/30 focus:border-white'
          )}
        />
        {errors.message && (
          <p role="alert" className={errorClass}>{errors.message.message}</p>
        )}
      </div>

      {/* Submit Button - Centered */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '48px' }}>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            border: '1px solid #fff',
            backgroundColor: 'transparent',
            paddingInline: '60px',
            paddingBlock: '20px',
            fontSize: '16px',
            fontWeight: '500',
            color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minWidth: '220px',
          }}
          className="hover:bg-white hover:text-[#173835] disabled:opacity-50"
        >
          {isSubmitting ? t.sending : t.submit}
        </button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <p role="status" className="mt-8 text-center text-green-400">
          {t.success}
        </p>
      )}
      {submitStatus === 'error' && (
        <p role="alert" className="mt-8 text-center text-red-500">
          {errorParts[0]}
          <bdi dir="ltr">{SUPPORT_EMAIL}</bdi>
          {errorParts[1] ?? ''}
        </p>
      )}
    </form>
  );
}
